import React, { useMemo, useState } from 'react'
import { Box, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'

import TopologyCaption from '../../../Topology/Caption'
import TopologyDetailCard from '../../../Topology/CardDetail'
import TopologyTree from '../../../Topology/Tree'
import { buildTopologyParentTree, getEquipmentInfo, normalizeMac } from '../../../Topology/utils'
import { Widget } from './Widget'

const flattenParentTree = tree => tree
  ? [tree, ...flattenParentTree(tree.children?.[0])]
  : []

const TopologyPreview = ({ equipment, topology, loading }) => {
  const { t } = useTranslation()
  const [selectedMac, setSelectedMac] = useState(null)
  const [hoveredNode, setHoveredNode] = useState(null)
  const treeData = useMemo(
    () => buildTopologyParentTree(topology, equipment?.mac),
    [equipment?.mac, topology]
  )
  const nodes = useMemo(() => flattenParentTree(treeData), [treeData])
  const targetNode = nodes[nodes.length - 1]
  const selectedNode = selectedMac
    ? nodes.find(node => normalizeMac(node.mac_address) === selectedMac)
    : null
  const activeNode = selectedNode || targetNode
  const existingTypes = useMemo(() => {
    const types = new Set(nodes.map(node => node.type || 'other'))
    return [...types].map(getEquipmentInfo)
  }, [nodes])

  const handleSelectNode = (node) => {
    const mac = normalizeMac(node.mac_address)
    setSelectedMac(current => current === mac ? null : mac)
  }

  return (
    <Widget
      icon="fa-solid fa-share-nodes"
      title={t('supervision.hardware.detail.simple_topology')}
      sx={{ flex: 2, position: 'relative', overflow: 'hidden' }}
    >
      <Box
        sx={{
          flex: 1,
          position: 'relative',
          minHeight: 220,
          backgroundColor: '#FAFAFA',
          borderRadius: '16px',
          overflow: 'hidden'
        }}
      >
        {loading && (
          <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography sx={{ fontSize: 12, color: '#989898' }}>{t('ui_elements.loading')}</Typography>
          </Box>
        )}
        {!loading && !treeData && (
          <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography sx={{ fontSize: 12, color: '#989898' }}>{t('supervision.topology.missing')}</Typography>
          </Box>
        )}
        {!loading && treeData && (
          <TopologyTree
            treeData={treeData}
            selectedMac={activeNode?.mac_address}
            onSelectNode={handleSelectNode}
            onHoverNode={setHoveredNode}
            onUnhoverNode={() => setHoveredNode(null)}
            preview
          />
        )}

        <TopologyDetailCard
          equipment={hoveredNode || selectedNode}
          onClose={() => setSelectedMac(null)}
        />

        <TopologyCaption
          existingTypes={existingTypes}
          readonly
          defaultVisible={false}
          sx={{
            bottom: 8,
            maxHeight: 'calc(100% - 16px)',
            overflowY: 'auto',
            boxSizing: 'border-box',
            scrollbarWidth: 'none',
            '&::-webkit-scrollbar': { display: 'none' }
          }}
        />
      </Box>
    </Widget>
  )
}

export default TopologyPreview
